/**
 * Runtime DSL Context — builds WorkflowRegistries from embedded DSL data,
 * optionally merging with project-level DSL extensions.
 *
 * Default flow:
 *   CLI binary embeds resolved DSL data (from code generation) →
 *   loadDslContext() builds registries from it.
 *
 * Extended flow:
 *   Project provides a DSL that extends/overrides the embedded base →
 *   loadDslContext() merges via agent-contracts' mergeDsl() →
 *   builds registries from the merged result.
 */

import { readFile } from "node:fs/promises";
import type { z } from "zod";
import YAML from "yaml";
import type { WorkflowRegistries, WorkflowContractLike, WorkflowStep } from "./workflow-runner.js";
import type { AgentContractLike, TaskContractLike } from "./task-runner.js";
import { jsonSchemaToZod } from "./schema-converter.js";
import type { GuardrailRule, GuardrailRuleData } from "./guardrail-hooks.js";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface LoadDslOptions {
  /**
   * Pre-resolved DSL data embedded in the CLI binary.
   * Produced by the code generator alongside TypeScript contracts.
   * This is the default DSL — used as-is when no project extension exists.
   */
  embeddedDsl: Record<string, unknown>;

  /**
   * Path to a project-level DSL file that extends/overrides the embedded DSL.
   * When provided, the project DSL is resolved (including its own extends chain)
   * and merged with embeddedDsl via mergeDsl().
   */
  projectDslPath?: string;

  /** Entities the CLI depends on — validated after merge. */
  requiredEntities?: RequiredEntities;
}

export interface RequiredEntities {
  workflows?: string[];
  tasks?: string[];
}

export interface DslContext {
  readonly registries: WorkflowRegistries;
  readonly dsl: Record<string, unknown>;
  readonly guardrailRules?: GuardrailRuleData;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class DslValidationError extends Error {
  readonly missing: string[];
  constructor(missing: string[]) {
    super(
      "Required entities missing after DSL merge:\n" +
      missing.map((m) => `  - ${m}`).join("\n"),
    );
    this.name = "DslValidationError";
    this.missing = missing;
  }
}

// ---------------------------------------------------------------------------
// Internal: build registries from resolved DSL
// ---------------------------------------------------------------------------

function buildAgentRegistry(
  agents: Record<string, Record<string, unknown>>,
): Record<string, AgentContractLike> {
  const registry: Record<string, AgentContractLike> = {};
  for (const [id, agent] of Object.entries(agents)) {
    const rawMode = (agent.mode as string) ?? "read-only";
    const mode: "read-write" | "read-only" = rawMode === "read-write" ? "read-write" : "read-only";
    registry[id] = {
      id,
      role_name: (agent.role_name as string) ?? id,
      purpose: (agent.purpose as string) ?? "",
      mode,
      responsibilities: (agent.responsibilities as string[]) ?? [],
      constraints: (agent.constraints as string[]) ?? [],
      rules: (agent.rules as Array<{ id: string; description: string; severity: string }>) ?? [],
    };
  }
  return registry;
}

function buildTaskRegistry(
  tasks: Record<string, Record<string, unknown>>,
): Record<string, TaskContractLike> {
  const registry: Record<string, TaskContractLike> = {};
  for (const [id, task] of Object.entries(tasks)) {
    registry[id] = {
      id,
      description: (task.description as string) ?? "",
      target_agent: (task.target_agent as string) ?? "",
      result_handoff: (task.result_handoff as string) ?? "",
      completion_criteria: (task.completion_criteria as string[]) ?? [],
      model_class: task.model_class as "fast" | "standard" | "thinking" | undefined,
    };
  }
  return registry;
}

function buildWorkflowStep(step: Record<string, unknown>): WorkflowStep {
  if (step.type === "gate") {
    return {
      type: "gate" as const,
      gate_kind: (step.gate_kind as string) ?? "",
      description: (step.description as string) ?? "",
      depends_on: step.depends_on as readonly string[] | undefined,
    };
  }
  return {
    type: "delegate" as const,
    task: (step.task as string) ?? "",
    from_agent: (step.from_agent as string) ?? "",
    description: (step.description as string) ?? "",
    optional: (step.optional as boolean) ?? false,
    max_retries: (step.max_retries as number) ?? 0,
    max_follow_ups: step.max_follow_ups as number | undefined,
    depends_on: step.depends_on as readonly string[] | undefined,
    retry: step.retry as { condition: string; fix_task: string; revalidate_task?: string } | undefined,
  };
}

function buildWorkflowRegistry(
  workflows: Record<string, Record<string, unknown>>,
): Record<string, WorkflowContractLike> {
  const registry: Record<string, WorkflowContractLike> = {};
  for (const [id, wf] of Object.entries(workflows)) {
    const steps = ((wf.steps as Record<string, unknown>[]) ?? []).map(buildWorkflowStep);
    registry[id] = { id, description: (wf.description as string) ?? "", steps };
  }
  return registry;
}

function buildHandoffSchemas(
  handoffTypes: Record<string, Record<string, unknown>>,
): Record<string, z.ZodType> {
  const schemas: Record<string, z.ZodType> = {};
  for (const [id, ht] of Object.entries(handoffTypes)) {
    const schema = ht.schema as Record<string, unknown> | undefined;
    if (schema) {
      schemas[id] = jsonSchemaToZod(schema);
    }
  }
  return schemas;
}

// ---------------------------------------------------------------------------
// Build registries from a resolved DSL object
// ---------------------------------------------------------------------------

export function buildGuardrailRulesFromDsl(
  dsl: Record<string, unknown>,
): GuardrailRuleData | undefined {
  const raw = dsl._guardrailRules as Record<string, unknown> | undefined;
  if (!raw) return undefined;
  return {
    commandRules: (raw.commandRules as GuardrailRule[]) ?? [],
    fileRules: (raw.fileRules as GuardrailRule[]) ?? [],
    contentRules: (raw.contentRules as GuardrailRule[]) ?? [],
  };
}

export function buildRegistriesFromDsl(
  dsl: Record<string, unknown>,
): WorkflowRegistries {
  const agents = (dsl.agents as Record<string, Record<string, unknown>>) ?? {};
  const tasks = (dsl.tasks as Record<string, Record<string, unknown>>) ?? {};
  const workflows = (dsl.workflow as Record<string, Record<string, unknown>>) ?? {};
  const handoffTypes = (dsl.handoff_types as Record<string, Record<string, unknown>>) ?? {};

  return {
    agentRegistry: buildAgentRegistry(agents),
    taskRegistry: buildTaskRegistry(tasks),
    workflowRegistry: buildWorkflowRegistry(workflows),
    handoffSchemas: buildHandoffSchemas(handoffTypes),
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function validateRequiredEntities(
  dsl: Record<string, unknown>,
  required: RequiredEntities,
): void {
  const missing: string[] = [];

  const workflows = (dsl.workflow as Record<string, unknown>) ?? {};
  const tasks = (dsl.tasks as Record<string, unknown>) ?? {};
  const handoffTypes = (dsl.handoff_types as Record<string, unknown>) ?? {};

  if (required.workflows) {
    for (const wfId of required.workflows) {
      if (!(wfId in workflows)) {
        missing.push(`workflow "${wfId}" is required by the CLI but not found in the resolved DSL`);
      }
    }
  }

  if (required.tasks) {
    for (const taskId of required.tasks) {
      if (!(taskId in tasks)) {
        missing.push(`task "${taskId}" is required by the CLI but not found in the resolved DSL`);
        continue;
      }
      const task = tasks[taskId] as Record<string, unknown>;
      const invHandoff = task.invocation_handoff as string | undefined;
      const resHandoff = task.result_handoff as string | undefined;
      if (invHandoff && !(invHandoff in handoffTypes)) {
        missing.push(`handoff_type "${invHandoff}" (invocation_handoff of task "${taskId}") not found`);
      }
      if (resHandoff && !(resHandoff in handoffTypes)) {
        missing.push(`handoff_type "${resHandoff}" (result_handoff of task "${taskId}") not found`);
      }
    }
  }

  if (required.workflows) {
    for (const wfId of required.workflows) {
      if (!(wfId in workflows)) continue;
      const wf = workflows[wfId] as Record<string, unknown>;
      const steps = (wf.steps as Array<Record<string, unknown>>) ?? [];
      for (const step of steps) {
        if (step.type !== "delegate") continue;
        const taskId = step.task as string;
        if (taskId && taskId in tasks) {
          const task = tasks[taskId] as Record<string, unknown>;
          const resHandoff = task.result_handoff as string | undefined;
          if (resHandoff && !(resHandoff in handoffTypes)) {
            missing.push(`handoff_type "${resHandoff}" (result of task "${taskId}" in workflow "${wfId}") not found`);
          }
        }
      }
    }
  }

  if (missing.length > 0) {
    throw new DslValidationError(missing);
  }
}

// ---------------------------------------------------------------------------
// Main API
// ---------------------------------------------------------------------------

/**
 * Build a DslContext from embedded DSL data, optionally merging with
 * a project-level DSL extension.
 *
 * Default (no projectDslPath):
 *   Builds registries directly from embeddedDsl — no filesystem access.
 *
 * Extended (with projectDslPath):
 *   Resolves the project DSL from filesystem, merges it over embeddedDsl
 *   via agent-contracts' mergeDsl(), then builds registries from the result.
 */
export async function loadDslContext(
  options: LoadDslOptions,
): Promise<DslContext> {
  let dsl: Record<string, unknown>;

  if (options.projectDslPath) {
    const { resolve: resolveDsl, mergeDsl, expandDefaults } = await import("agent-contracts");
    const raw = await readFile(options.projectDslPath, "utf8");
    const projectData = YAML.parse(raw) as Record<string, unknown>;

    if (projectData.extends === "/$embedded/") {
      const { extends: _extends, ...projectOverrides } = projectData;
      const merged = mergeDsl(options.embeddedDsl, projectOverrides);
      dsl = expandDefaults(merged);
    } else {
      const projectResult = await resolveDsl(options.projectDslPath);
      const merged = mergeDsl(options.embeddedDsl, projectResult.data);
      dsl = expandDefaults(merged);
    }
  } else {
    dsl = options.embeddedDsl;
  }

  if (options.requiredEntities) {
    validateRequiredEntities(dsl, options.requiredEntities);
  }

  const registries = buildRegistriesFromDsl(dsl);
  const guardrailRules = buildGuardrailRulesFromDsl(dsl);

  return { registries, dsl, guardrailRules };
}
